/**
 * TaskBoardFilters Component
 *
 * Modern filter interface with grouped layout:
 * - Primary filters (Assignee, Status, Due Date) in top row
 * - Secondary filters grouped by category below
 * - All filters visible by default (no progressive disclosure)
 * - Visual grouping improves scannability
 *
 * Hierarchy: Primary Filters > Secondary Filters (grouped) > Options
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Select,
  Button,
  Checkbox,
  Tooltip,
  DatePicker,
  Badge,
  Tag,
  Space,
  Dropdown,
  Menu,
  Collapse,
} from 'antd';
import {
  FilterOutlined,
  CloseOutlined,
  DownOutlined,
  SaveOutlined,
  CloudDownloadOutlined,
  ClearOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import moment from 'moment';
import {
  taskTypes,
  subTypes,
  statusTypes,
  advertiserTypes,
  urgencyTypes,
  urgencyColors,
} from '../../configs/task.config';
import { space } from '../../configs/designTokens';
import '../../styles/index.scss';

const { Option, OptGroup } = Select;
const { Panel } = Collapse;

const TaskBoardFilters = ({
  windowWidth,
  showFilters,
  setShowFilters,
  searchParams,
  setSearchParams,
  level,
  showMyTasks,
  isKanban,
  // Filter handlers
  handleAssigneeFilter,
  handleRequestorFilter,
  handleTaskTypeFilter,
  handleTaskSubTypeFilter,
  handleStatusFilter,
  handleAdvertiserTypeFilter,
  handleAdvertiserFilter,
  handleUrgencyFilter,
  handleDueDateFilter,
  // User/Org data
  uniqueUsers,
  uniqueRequestors,
  basicOrgsDetails,
  // Loading states
  basicUsersDetailsLoading,
  requestorLoading,
  basicOrgsDetailsLoading,
  // Fetch functions
  fetchBasicUsers,
  fetchBasicUsersRequestor,
  fetchAdvertiserOrgs,
  // Filter actions
  visibleColumns,
  setVisibleColumns,
  projectFilter,
  setProjectFilter,
  onClearFilters,
  onSaveFilters,
  onLoadFilters,
  hasActiveFilters,
  onPaginationReset,
}) => {
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 992;

  // Calculate active filter count and chips
  const activeFilters = useMemo(() => {
    const filters = [];

    if (searchParams.assigneeNames) {
      const names = searchParams.assigneeNames.split(',');
      filters.push({
        key: 'assignee',
        label: `Assignee: ${names.length}`,
        category: 'assignee',
        onRemove: () => handleAssigneeFilter([]),
      });
    }

    if (searchParams.requestor) {
      const requestors = searchParams.requestor.split(',');
      filters.push({
        key: 'requestor',
        label: `Requestor: ${requestors.length}`,
        category: 'assignee',
        onRemove: () => handleRequestorFilter([]),
      });
    }

    if (searchParams.requestTypes) {
      filters.push({
        key: 'type',
        label: `Type: ${searchParams.requestTypes.split(',').length}`,
        category: 'type',
        onRemove: () => handleTaskTypeFilter([]),
      });
    }

    if (searchParams.requestSubTypes) {
      filters.push({
        key: 'subtype',
        label: `Subtype: ${searchParams.requestSubTypes.split(',').length}`,
        category: 'type',
        onRemove: () => handleTaskSubTypeFilter([]),
      });
    }

    if (searchParams.status) {
      filters.push({
        key: 'status',
        label: `Status: ${searchParams.status.split(',').length}`,
        category: 'status',
        onRemove: () => handleStatusFilter([]),
      });
    }

    if (searchParams.urgency) {
      filters.push({
        key: 'urgency',
        label: `Urgency: ${searchParams.urgency.split(',').length}`,
        category: 'urgency',
        onRemove: () => handleUrgencyFilter([]),
      });
    }

    if (searchParams.dueDate) {
      filters.push({
        key: 'dueDate',
        label: 'Due Date',
        category: 'date',
        onRemove: () => handleDueDateFilter(''),
      });
    }

    if (searchParams.advertiser) {
      filters.push({
        key: 'advertiser',
        label: `Advertiser: ${searchParams.advertiser.split(',').length}`,
        category: 'type',
        onRemove: () => handleAdvertiserFilter([]),
      });
    }

    return filters;
  }, [searchParams, handleAssigneeFilter, handleRequestorFilter, handleTaskTypeFilter, handleTaskSubTypeFilter, handleStatusFilter, handleUrgencyFilter, handleDueDateFilter, handleAdvertiserFilter]);

  // Filter presets menu
  const filterPresetsMenu = (
    <Menu>
      <Menu.Item key="save" icon={<SaveOutlined />} onClick={onSaveFilters}>
        Save Current Filters
      </Menu.Item>
      <Menu.Item key="load" icon={<CloudDownloadOutlined />} onClick={onLoadFilters}>
        Load Saved Filters
      </Menu.Item>
    </Menu>
  );

  // Render compact filter select
  const renderFilterSelect = ({
    placeholder,
    value,
    onChange,
    options,
    loading,
    onSearch,
    showSearch = false,
    mode = 'multiple',
  }) => (
    <Select
      className="task-filter-select"
      dropdownClassName="task-filter-dropdown"
      mode={mode}
      showSearch={showSearch}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      loading={loading}
      onSearch={onSearch}
      maxTagCount={0}
      maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
      allowClear
      filterOption={(input, option) =>
        option?.children?.toLowerCase?.()?.indexOf?.(input.toLowerCase()) >= 0
      }
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 200 }}
    >
      {options}
    </Select>
  );

  return (
    <div className="task-board-filters-modern">
      {/* Filter Panel - Progressive Disclosure */}
      {showFilters && (
        <div className="task-filters__container">
          {/* Active Filter Chips */}
          {activeFilters.length > 0 && (
            <div className="task-filters__chips-bar">
              <div className="task-filters__chips">
                {activeFilters.slice(0, isMobile ? 2 : 5).map((filter) => (
                  <Tag
                    key={filter.key}
                    className={`task-filter-chip task-filter-chip--${filter.category}`}
                    closable
                    onClose={filter.onRemove}
                  >
                    {filter.label}
                  </Tag>
                ))}
                {activeFilters.length > (isMobile ? 2 : 5) && (
                  <Tag className="task-filter-chip task-filter-chip--more">
                    +{activeFilters.length - (isMobile ? 2 : 5)} more
                  </Tag>
                )}
              </div>
              <Button
                className="task-filters__clear-link"
                type="link"
                size="small"
                onClick={onClearFilters}
              >
                Clear all
              </Button>
            </div>
          )}

          {/* Primary Filters Row - Always visible (Assignee, Status, Due) */}
          <div className="task-filters__primary">
            <div className="task-filters__primary-row">
              {/* Assignee */}
              <div className="task-filters__primary-item">
                {renderFilterSelect({
                  placeholder: 'Assignee',
                  value: searchParams.assigneeNames ? searchParams.assigneeNames.split(',') : [],
                  onChange: handleAssigneeFilter,
                  loading: basicUsersDetailsLoading,
                  onSearch: fetchBasicUsers,
                  showSearch: true,
                  options: uniqueUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {startCase(user.name || user.username)}
                    </Option>
                  )),
                })}
              </div>

              {/* Status */}
              <div className="task-filters__primary-item">
                {renderFilterSelect({
                  placeholder: 'Status',
                  value: searchParams.status ? searchParams.status.split(',') : [],
                  onChange: handleStatusFilter,
                  options: Object.keys(statusTypes).map((status) => (
                    <Option key={status} value={statusTypes[status]}>
                      {status}
                    </Option>
                  )),
                })}
              </div>

              {/* Due Date */}
              <div className="task-filters__primary-item task-filters__primary-item--date">
                <DatePicker.RangePicker
                  className="task-filter-datepicker"
                  placeholder={isMobile ? ['From', 'To'] : ['Due From', 'Due To']}
                  value={
                    searchParams.dueDate && searchParams.dueDate.split(',').length === 2
                      ? [
                          moment(searchParams.dueDate.split(',')[0], 'YYYY-MM-DD'),
                          moment(searchParams.dueDate.split(',')[1], 'YYYY-MM-DD'),
                        ]
                      : []
                  }
                  onChange={(dates) =>
                    handleDueDateFilter(
                      dates && dates.length === 2
                        ? dates.map((d) => d.format('YYYY-MM-DD')).join(',')
                        : ''
                    )
                  }
                  allowClear
                  format="MMM D"
                  suffixIcon={<CalendarOutlined />}
                  popupStyle={{
                    maxWidth: isMobile ? '90vw' : 'auto',
                    left: isMobile ? '50%' : undefined,
                    transform: isMobile ? 'translateX(-50%)' : undefined,
                  }}
                  dropdownClassName={isMobile ? 'task-mobile-range-picker-dropdown' : ''}
                  getPopupContainer={isMobile ? (trigger) => trigger.parentElement : undefined}
                />
              </div>

              {/* Close */}
              <button
                className="task-filters__close-btn"
                onClick={() => setShowFilters(false)}
                aria-label="Close filters"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>

          <div className="task-filters__secondary">
              {/* People Group */}
              <div className="task-filters__group">
                <span className="task-filters__group-label">People</span>
                <div className="task-filters__group-items">
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Requestor',
                      value: searchParams.requestor ? searchParams.requestor.split(',') : [],
                      onChange: handleRequestorFilter,
                      loading: requestorLoading,
                      onSearch: fetchBasicUsersRequestor,
                      showSearch: true,
                      options: uniqueRequestors.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {startCase(user.name || user.username)}
                        </Option>
                      )),
                    })}
                  </div>
                </div>
              </div>

              {/* Type Group */}
              <div className="task-filters__group">
                <span className="task-filters__group-label">Type</span>
                <div className="task-filters__group-items">
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Type',
                      value: searchParams.requestTypes ? searchParams.requestTypes.split(',') : [],
                      onChange: handleTaskTypeFilter,
                      options: taskTypes.map((type) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      )),
                    })}
                  </div>
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Subtype',
                      value: searchParams.requestSubTypes ? searchParams.requestSubTypes.split(',') : [],
                      onChange: handleTaskSubTypeFilter,
                      options: Object.entries(subTypes).map(([mainType, subTypeArray]) => (
                        <OptGroup label={mainType} key={mainType}>
                          {subTypeArray.map((subType) => (
                            <Option key={`${mainType}-${subType}`} value={subType}>
                              {subType}
                            </Option>
                          ))}
                        </OptGroup>
                      )),
                    })}
                  </div>
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Urgency',
                      value: searchParams.urgency ? searchParams.urgency.split(',') : [],
                      onChange: handleUrgencyFilter,
                      options: Object.keys(urgencyTypes).map((urgency) => (
                        <Option key={urgency} value={urgencyTypes[urgency]}>
                          <Badge
                            color={urgencyColors[urgencyTypes[urgency]]}
                            style={{ marginRight: space[2] }}
                          />
                          {urgency}
                        </Option>
                      )),
                    })}
                  </div>
                </div>
              </div>

              {/* Organization Group (Platform only) */}
              {level !== 'CHANNEL_PARTNER' && (
                <div className="task-filters__group">
                  <span className="task-filters__group-label">Organization</span>
                  <div className="task-filters__group-items">
                    <div className="task-filters__group-item">
                      {renderFilterSelect({
                        placeholder: 'Adv. Type',
                        value: searchParams.advertiserTypes ? searchParams.advertiserTypes.split(',') : [],
                        onChange: handleAdvertiserTypeFilter,
                        options: Object.keys(advertiserTypes).map((type) => (
                          <Option key={type} value={advertiserTypes[type]}>
                            {type}
                          </Option>
                        )),
                      })}
                    </div>
                    <div className="task-filters__group-item">
                      {renderFilterSelect({
                        placeholder: 'Advertiser',
                        value: searchParams.advertiser ? searchParams.advertiser.split(',') : [],
                        onChange: handleAdvertiserFilter,
                        loading: basicOrgsDetailsLoading,
                        onSearch: fetchAdvertiserOrgs,
                        showSearch: true,
                        options: basicOrgsDetails.map((org) => (
                          <Option key={org.id} value={org.name}>
                            {org.name}
                          </Option>
                        )),
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Options Group */}
              <div className="task-filters__group task-filters__group--options">
                <div className="task-filters__options-row">
                  {!isKanban && (
                    <Checkbox
                      className="task-filters__checkbox"
                      checked={searchParams.showAllStatuses === true || searchParams.showAllStatuses === 'true'}
                      onChange={(e) => {
                        setSearchParams({ ...searchParams, showAllStatuses: e.target.checked });
                        onPaginationReset();
                      }}
                    >
                      Show all statuses
                    </Checkbox>
                  )}

                  {showMyTasks && (
                    <>
                      <Checkbox
                        className="task-filters__checkbox"
                        checked={visibleColumns.includes('following')}
                        onChange={(e) => {
                          const newColumns = e.target.checked
                            ? [...visibleColumns, 'following']
                            : visibleColumns.filter((c) => c !== 'following');
                          setVisibleColumns(newColumns);
                          setSearchParams({ ...searchParams, following: e.target.checked });
                        }}
                      >
                        Following
                      </Checkbox>
                      {level === 'PLATFORM' && (
                        <Checkbox
                          className="task-filters__checkbox"
                          checked={visibleColumns.includes('collaborating')}
                          onChange={(e) => {
                            const newColumns = e.target.checked
                              ? [...visibleColumns, 'collaborating']
                              : visibleColumns.filter((c) => c !== 'collaborating');
                            setVisibleColumns(newColumns);
                            setSearchParams({ ...searchParams, collaborating: e.target.checked });
                          }}
                        >
                          Collaborating
                        </Checkbox>
                      )}
                    </>
                  )}

                  {level === 'PLATFORM' && (
                    <div className="task-filters__project-toggle">
                      <span>Projects:</span>
                      <Checkbox
                        checked={projectFilter === 'projectOnly'}
                        onChange={(e) => {
                          setProjectFilter(e.target.checked ? 'projectOnly' : 'all');
                          onPaginationReset();
                        }}
                      >
                        Only
                      </Checkbox>
                      <Checkbox
                        checked={projectFilter === 'noProject'}
                        onChange={(e) => {
                          setProjectFilter(e.target.checked ? 'noProject' : 'all');
                          onPaginationReset();
                        }}
                      >
                        Exclude
                      </Checkbox>
                    </div>
                  )}

                  <div className="task-filters__preset-actions">
                    <Button
                      className="task-filters__preset-btn"
                      icon={<CloudDownloadOutlined />}
                      onClick={onLoadFilters}
                    >
                      Load Filters
                    </Button>
                    <Button
                      className="task-filters__preset-btn task-filters__preset-btn--save"
                      icon={<SaveOutlined />}
                      onClick={onSaveFilters}
                      type="primary"
                    >
                      Save Filters
                    </Button>
                  </div>
                </div>
              </div>
            </div>
        </div>
      )}

      {/* Mobile: Active Filters Summary */}
      {isMobile && activeFilters.length > 0 && !showFilters && (
        <div className="task-filters__mobile-summary">
          <span>{activeFilters.length} filter{activeFilters.length > 1 ? 's' : ''} active</span>
          <Button type="link" size="small" onClick={onClearFilters}>
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
};

TaskBoardFilters.propTypes = {
  windowWidth: PropTypes.number.isRequired,
  showFilters: PropTypes.bool.isRequired,
  setShowFilters: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  setSearchParams: PropTypes.func.isRequired,
  level: PropTypes.string,
  showMyTasks: PropTypes.bool,
  isKanban: PropTypes.bool,
  // Filter handlers
  handleAssigneeFilter: PropTypes.func.isRequired,
  handleRequestorFilter: PropTypes.func.isRequired,
  handleTaskTypeFilter: PropTypes.func.isRequired,
  handleTaskSubTypeFilter: PropTypes.func.isRequired,
  handleStatusFilter: PropTypes.func.isRequired,
  handleAdvertiserTypeFilter: PropTypes.func.isRequired,
  handleAdvertiserFilter: PropTypes.func.isRequired,
  handleUrgencyFilter: PropTypes.func.isRequired,
  handleDueDateFilter: PropTypes.func.isRequired,
  // User/Org data
  uniqueUsers: PropTypes.array.isRequired,
  uniqueRequestors: PropTypes.array.isRequired,
  basicOrgsDetails: PropTypes.array.isRequired,
  // Loading states
  basicUsersDetailsLoading: PropTypes.bool,
  requestorLoading: PropTypes.bool,
  basicOrgsDetailsLoading: PropTypes.bool,
  // Fetch functions
  fetchBasicUsers: PropTypes.func.isRequired,
  fetchBasicUsersRequestor: PropTypes.func.isRequired,
  fetchAdvertiserOrgs: PropTypes.func.isRequired,
  // Filter actions
  visibleColumns: PropTypes.array.isRequired,
  setVisibleColumns: PropTypes.func.isRequired,
  projectFilter: PropTypes.string,
  setProjectFilter: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onSaveFilters: PropTypes.func.isRequired,
  onLoadFilters: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool,
  onPaginationReset: PropTypes.func.isRequired,
};

TaskBoardFilters.defaultProps = {
  level: null,
  showMyTasks: false,
  isKanban: false,
  basicUsersDetailsLoading: false,
  requestorLoading: false,
  basicOrgsDetailsLoading: false,
  projectFilter: 'all',
  hasActiveFilters: false,
};

export default TaskBoardFilters;
